export const DATE_PRESETS = ['all', 'week', 'month', 'months', 'day'] as const;

export type DatePreset = (typeof DATE_PRESETS)[number];

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Compare a Pre-Event's advertised calendar day with the visitor's calendar.
 *
 * `wallDay` is the YYYY-MM-DD written on the Pre-Event, not the visitor's
 * timezone conversion of the instant.
 */
export const startDateInWindow = (
  wallDay: string | undefined,
  preset: DatePreset,
  day: string,
  now = new Date(),
): boolean => {
  if (preset === 'all' || (preset === 'day' && day === '')) {
    return true;
  }

  const eventDay = dayNumber(wallDay);
  if (eventDay == null) {
    return false;
  }

  if (preset === 'day') {
    const picked = dayNumber(day);
    return picked != null && eventDay === picked;
  }

  if (preset === 'week') {
    const monday = mondayOf(now);
    const sunday = new Date(
      monday.getFullYear(),
      monday.getMonth(),
      monday.getDate() + 6,
    );
    return inInclusive(eventDay, localDayNumber(monday), localDayNumber(sunday));
  }

  if (preset === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return inInclusive(eventDay, localDayNumber(start), localDayNumber(end));
  }

  const end = new Date(now.getFullYear(), now.getMonth() + 3, 0);
  return inInclusive(eventDay, localDayNumber(now), localDayNumber(end));
};

export const formatLocalDay = (ymd: string): string => {
  const parts = parseYmd(ymd);
  if (parts == null) {
    return ymd;
  }

  return `${parts.day}/${parts.month}/${parts.year}`;
};

const parseYmd = (
  ymd: string,
): { year: string; month: string; day: string } | undefined => {
  const match = YMD.exec(ymd);
  if (
    match == null ||
    match[1] == null ||
    match[2] == null ||
    match[3] == null
  ) {
    return undefined;
  }

  return { year: match[1], month: match[2], day: match[3] };
};

const dayNumber = (ymd: string | undefined): number | undefined => {
  if (ymd == null) {
    return undefined;
  }

  const parts = parseYmd(ymd);
  if (parts == null) {
    return undefined;
  }

  return Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
  );
};

const localDayNumber = (value: Date): number =>
  Date.UTC(value.getFullYear(), value.getMonth(), value.getDate());

const mondayOf = (now: Date): Date => {
  const weekday = now.getDay();
  const offset = weekday === 0 ? -6 : 1 - weekday;
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
};

const inInclusive = (value: number, from: number, to: number): boolean =>
  value >= from && value <= to;
